import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import api from "../lib/api";
import SocialExport from "../components/SocialExport";
import { CoverSheet, ArticleSheet, ForumSheet, RED } from "../components/EditionSheets";
import { CoverStory, CoverPost, ArticleStory, ArticlePost, ForumStory, ForumPost } from "../components/EditionPosters";

function PageBlock({ id, n, label, share, children }) {
  return (
    <section id={id} data-testid={`special-page-${n}`} className="scroll-mt-20">
      <div className="mx-auto max-w-[880px] flex items-center gap-3 mb-4 px-1">
        <span className="w-6 h-[2px]" style={{ background: RED }} aria-hidden />
        <span className="text-[0.62rem] tracking-[0.26em] uppercase font-bold text-[var(--gai-grey-600)]">
          Page {n} / 3 · {label}
        </span>
      </div>
      {children}
      <div className="mx-auto max-w-[880px] mt-5 flex items-center justify-between gap-3 flex-wrap px-1">
        <span className="text-[0.62rem] tracking-[0.26em] uppercase font-bold text-[var(--gai-grey-500)]">{share.label}</span>
        {share.node}
      </div>
    </section>
  );
}

export default function SpecialEdition() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const isFR = lang === "fr";
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/special-editions/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  if (notFound) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-28 text-center" data-testid="special-not-found">
        <div className="overline mb-3 text-[var(--gai-red)]">{isFR ? "Édition introuvable" : "Edition not found"}</div>
        <h1 className="serif text-4xl text-[var(--gai-black)]">
          {isFR ? "Cette édition spéciale n'existe pas." : "This special edition doesn't exist."}
        </h1>
        <Link to="/articles" className="gai-btn-gold mt-8 inline-flex">{isFR ? "Voir toutes les actualités" : "All news"}</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="max-w-[800px] mx-auto px-6 py-28 text-sm text-[var(--gai-grey-500)]">{isFR ? "Chargement…" : "Loading…"}</div>;
  }

  const exportFor = (key, Story, Post) => (
    <SocialExport
      filename={`gai-${data.slug}-${key}`}
      storyPoster={<Story data={data} lang={lang} />}
      postPoster={<Post data={data} lang={lang} />}
      testidPrefix={`export-${key}`}
    />
  );

  const navItems = [
    ["page-1", `01 · ${isFR ? "Couverture" : "Cover"}`],
    ["page-2", "02 · Article"],
    ["page-3", `03 · ${isFR ? "Le Forum" : "The Forum"}`],
  ];

  return (
    <div style={{ background: "#EFEFEC" }} data-testid={`page-special-${data.slug}`}>
      <div className="sticky top-0 z-40 border-b" style={{ background: "rgba(255,255,255,0.95)", backdropFilter: "blur(8px)", borderColor: "var(--gai-grey-200)" }}>
        <div className="max-w-[1320px] mx-auto px-4 lg:px-10 h-14 flex items-center justify-between gap-4">
          <Link to="/" className="text-[0.68rem] tracking-[0.22em] uppercase text-[var(--gai-grey-600)] hover:text-[var(--gai-red)]" data-testid="special-back">
            {isFR ? "← Accueil" : "← Home"}
          </Link>
          <div className="hidden md:flex items-center gap-5 text-[0.65rem] tracking-[0.2em] uppercase">
            {navItems.map(([id, label], i) => (
              <button key={id} onClick={() => scrollTo(id)} className="text-[var(--gai-black)] hover:text-[var(--gai-red)]" data-testid={`special-nav-${i + 1}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="text-[0.65rem] tracking-[0.22em] uppercase" style={{ color: RED }}>{isFR ? "Édition spéciale" : "Special edition"}</div>
        </div>
      </div>

      <div className="px-3 sm:px-6 py-10 md:py-16 space-y-16 md:space-y-24">
        <PageBlock id="page-1" n={1} label={isFR ? "Couverture" : "Cover"}
          share={{ label: isFR ? "Partager la couverture" : "Share the cover", node: exportFor("couverture", CoverStory, CoverPost) }}>
          <CoverSheet data={data} lang={lang} />
        </PageBlock>

        <PageBlock id="page-2" n={2} label="Article"
          share={{ label: isFR ? "Partager l'article" : "Share the article", node: exportFor("article", ArticleStory, ArticlePost) }}>
          <ArticleSheet data={data} lang={lang} />
        </PageBlock>

        <PageBlock id="page-3" n={3} label={isFR ? "Le Forum" : "The Forum"}
          share={{ label: isFR ? "Partager le Forum" : "Share the Forum", node: exportFor("forum", ForumStory, ForumPost) }}>
          <ForumSheet data={data} lang={lang} />
        </PageBlock>

        <div className="mx-auto max-w-[880px] flex flex-wrap gap-3 justify-center pb-4">
          <Link to="/annoncer" className="gai-btn-gold text-xs" data-testid="special-cta-annoncer">
            {isFR ? "Annoncer dans GAI" : "Advertise in GAI"} <ArrowRight size={13} />
          </Link>
          <Link to="/articles" className="gai-btn-outline text-xs" data-testid="special-cta-articles">
            {isFR ? "Toutes les actualités" : "All news"}
          </Link>
        </div>
      </div>
    </div>
  );
}
